import { Descriptions, Typography, Tag, List, Avatar, Button, Space } from 'antd'
import { 
  UserOutlined, 
  TeamOutlined, 
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  DownloadOutlined
} from '@ant-design/icons'
import { format } from 'date-fns'
import type { MessageWithAttachments } from '../../hooks/useRealtimeMessages'

const { Text, Paragraph, Title } = Typography

interface MessageDetailProps {
  message: MessageWithAttachments
  onClose?: () => void
}

export default function MessageDetail({ message, onClose }: MessageDetailProps) {
  const isAdminMessage = message.is_admin_message

  const handleDownloadAttachment = (attachment: MessageWithAttachments['attachments'][0]) => {
    // TODO: Implement file download from Supabase Storage
    console.log('Download attachment:', attachment)
  }

  return (
    <div className="message-detail">
      <div className="mb-4">
        <Avatar 
          size={48}
          icon={isAdminMessage ? <TeamOutlined /> : <UserOutlined />}
          style={{ 
            backgroundColor: isAdminMessage ? '#1890ff' : '#52c41a'
          }}
        />
        <Title level={4} className="mt-2 mb-0">
          {message.sender_name || (isAdminMessage ? 'Admin' : 'Reporter')}
        </Title>
        {message.sender_role && (
          <Tag color={isAdminMessage ? 'blue' : 'green'}>
            {message.sender_role}
          </Tag>
        )}
      </div>

      <Descriptions column={1} size="small" className="mb-4">
        <Descriptions.Item label="Sent">
          <Space>
            <ClockCircleOutlined />
            {message.created_at ? format(new Date(message.created_at), 'PPpp') : 'N/A'}
          </Space>
        </Descriptions.Item>
        
        {message.read_at && (
          <Descriptions.Item label="Read">
            <Space>
              <CheckCircleOutlined className="text-green-500" />
              {format(new Date(message.read_at), 'PPpp')}
            </Space>
          </Descriptions.Item>
        )}

        {message.metadata && Object.keys(message.metadata).length > 0 && (
          <Descriptions.Item label="Metadata">
            <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded">
              {JSON.stringify(message.metadata, null, 2)}
            </pre>
          </Descriptions.Item>
        )}
      </Descriptions>

      <div className="mb-4">
        <Text strong className="block mb-2">Message Content</Text>
        <Paragraph className="bg-gray-50 dark:bg-gray-800 p-3 rounded">
          {message.content}
        </Paragraph>
      </div>

      {message.attachments && message.attachments.length > 0 && (
        <div className="mb-4">
          <Text strong className="block mb-2">
            <FileTextOutlined className="mr-1" />
            Attachments ({message.attachments.length})
          </Text>
          <List
            size="small"
            dataSource={message.attachments}
            renderItem={(attachment) => (
              <List.Item
                actions={[
                  <Button
                    key="download"
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadAttachment(attachment)}
                  >
                    Download
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={<FileTextOutlined className="text-2xl text-gray-400" />}
                  title={attachment.file_name}
                  description={`${(attachment.file_size / 1024).toFixed(2)} KB • ${attachment.file_type}`}
                />
              </List.Item>
            )}
          />
        </div>
      )}

      <div className="mt-6">
        <Button block onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  )
}